import { get } from 'svelte/store';
import { profile_data } from 'src/stores/account/user';
import {
	BANNER_VIEWS_SCHEMA_VERSION,
	type BannerRecord,
	type BannerSurfaceState,
	type BannerViewsDocument,
} from 'src/types/banner-views';
import {
	CURY_APP_PROMO_BANNER_ID,
	CURY_APP_PROMO_LEGACY_LS_KEY,
	CURY_APP_PROMO_SURFACE,
	VERIVOX_OFFERS_BANNER_ID,
	VERIVOX_SETUP_BANNER_ID,
	VERIVOX_SNOOZE_MS,
	type VerivoxBannerSurface,
} from './banner-ids';
import { getBannerViews, replaceBannerViewsDocument } from './banner-views-helpers';
import { emptyBannerViewsDocument, mergeBannerViewsDocuments } from './banner-views-validate';

const DE_USERS_KEY = 'de_users';
const VERIVOX_INTEGRATION_KEY = 'verivox_integration';

type LegacyDismissalEntry = {
	dismissed?: boolean;
	dismissalDate?: number;
};

type LegacyVerivoxIntegration = {
	dismissals?: Partial<Record<VerivoxBannerSurface, LegacyDismissalEntry>>;
	dismissed?: boolean;
	dismissalDate?: number;
};

/**
 * One-shot: if localStorage still has the Cury dismiss flag and banner_views
 * has no cury record, seed a permanent shared dismiss.
 */
export const migrateCuryLocalStorageDismiss = (
	doc: BannerViewsDocument = getBannerViews(),
	storage: Storage | null = typeof localStorage !== 'undefined' ? localStorage : null,
	now: number = Date.now(),
): BannerViewsDocument => {
	if (doc.banners[CURY_APP_PROMO_BANNER_ID]) return doc;
	if (!storage) return doc;

	let dismissed = false;
	try {
		dismissed = Boolean(storage.getItem(CURY_APP_PROMO_LEGACY_LS_KEY));
	} catch {
		return doc;
	}
	if (!dismissed) return doc;

	const record: BannerRecord = {
		schemaVersion: BANNER_VIEWS_SCHEMA_VERSION,
		id: CURY_APP_PROMO_BANNER_ID,
		scope: 'shared',
		dismissedAt: now,
		lastDismissedAt: now,
		dismissCount: 1,
		dismissSource: 'user',
		surfaces: {
			[CURY_APP_PROMO_SURFACE]: {
				dismissedAt: now,
				lastDismissedAt: now,
				dismissCount: 1,
				dismissSource: 'user',
			},
		},
		extras: { migratedFrom: CURY_APP_PROMO_LEGACY_LS_KEY },
	};

	return mergeBannerViewsDocuments(doc, {
		schemaVersion: BANNER_VIEWS_SCHEMA_VERSION,
		banners: { [CURY_APP_PROMO_BANNER_ID]: record },
	});
};

export const clearCuryLegacyLocalStorage = (
	storage: Storage | null = typeof localStorage !== 'undefined' ? localStorage : null,
): void => {
	if (!storage) return;
	try {
		storage.removeItem(CURY_APP_PROMO_LEGACY_LS_KEY);
	} catch {
		// ignore
	}
};

const surfaceFromLegacy = (
	entry: LegacyDismissalEntry | undefined,
	now: number,
): BannerSurfaceState | null => {
	if (!entry?.dismissed) return null;
	const dismissedAt = entry.dismissalDate ?? now;
	return {
		dismissedAt,
		lastDismissedAt: dismissedAt,
		dismissCount: 1,
		dismissSource: 'user',
		snoozeUntil: dismissedAt + VERIVOX_SNOOZE_MS,
	};
};

/**
 * Map legacy de_users.verivox_integration.dismissals into banner_views
 * for setup + offers (same surfaces). Does not overwrite existing surfaces.
 */
export const migrateVerivoxLegacyDismissals = (
	doc: BannerViewsDocument = getBannerViews(),
	integration?: LegacyVerivoxIntegration | null,
	now: number = Date.now(),
): BannerViewsDocument => {
	const profile = get(profile_data);
	const source: LegacyVerivoxIntegration | null =
		integration ??
		(profile?.[DE_USERS_KEY]?.[VERIVOX_INTEGRATION_KEY] as LegacyVerivoxIntegration | undefined) ??
		null;

	if (!source || typeof source !== 'object') return doc;

	const dismissals: Partial<Record<VerivoxBannerSurface, LegacyDismissalEntry>> = {
		...(source.dismissals || {}),
	};
	if (source.dismissed && !dismissals.consumption) {
		dismissals.consumption = {
			dismissed: true,
			dismissalDate: source.dismissalDate ?? now,
		};
	}

	const surfaceEntries = Object.entries(dismissals) as [VerivoxBannerSurface, LegacyDismissalEntry][];
	if (surfaceEntries.length === 0) return doc;

	const applyToBanner = (bannerId: string, existing?: BannerRecord): BannerRecord => {
		const record: BannerRecord = existing
			? { ...existing, surfaces: { ...(existing.surfaces || {}) } }
			: {
					schemaVersion: BANNER_VIEWS_SCHEMA_VERSION,
					id: bannerId,
					scope: 'per-surface',
					surfaces: {},
				};

		for (const [surface, entry] of surfaceEntries) {
			if (record.surfaces?.[surface]) continue;
			const state = surfaceFromLegacy(entry, now);
			if (!state) continue;
			record.surfaces = {
				...(record.surfaces || {}),
				[surface]: { ...state, extras: { migratedFrom: 'de_users.dismissals' } },
			};
		}

		if (!record.extras) {
			record.extras = { migratedFrom: 'de_users.dismissals' };
		}
		return record;
	};

	const patch: BannerViewsDocument = {
		schemaVersion: BANNER_VIEWS_SCHEMA_VERSION,
		banners: {
			[VERIVOX_SETUP_BANNER_ID]: applyToBanner(
				VERIVOX_SETUP_BANNER_ID,
				doc.banners[VERIVOX_SETUP_BANNER_ID],
			),
			[VERIVOX_OFFERS_BANNER_ID]: applyToBanner(
				VERIVOX_OFFERS_BANNER_ID,
				doc.banners[VERIVOX_OFFERS_BANNER_ID],
			),
		},
	};

	return mergeBannerViewsDocuments(doc, patch);
};

/**
 * Run Cury + Verivox migrations and persist if the document changed.
 * Safe to call on banner mount / dismiss adapter entry.
 */
export const ensureBannerViewsMigrations = async (
	now: number = Date.now(),
): Promise<BannerViewsDocument> => {
	const before = getBannerViews();
	let next = migrateCuryLocalStorageDismiss(before, undefined, now);
	next = migrateVerivoxLegacyDismissals(next, undefined, now);

	const changed = JSON.stringify(before) !== JSON.stringify(next);
	if (changed) {
		const result = await replaceBannerViewsDocument(next);
		if (result.ok) {
			clearCuryLegacyLocalStorage();
			return result.document;
		}
		return before;
	}

	// Even if already migrated into profile, drop leftover localStorage flag.
	if (before.banners[CURY_APP_PROMO_BANNER_ID]) {
		clearCuryLegacyLocalStorage();
	}

	return before;
};

export const createEmptyBannerViewsForTests = emptyBannerViewsDocument;
